import { useEffect, useState } from "react";
import { api, ApiError } from "../api.js";
import { Icon } from "../components/Icon.js";
import { PinPad } from "../components/PinPad.js";
import type { Employee } from "../types.js";

/**
 * Consecutive runs of the same first letter, in the order the server
 * sent them. The server sorts; this only draws the breaks, so a grid
 * that arrives in some other order still shows every name exactly once.
 */
function byLetter(employees: Employee[]): { letter: string; people: Employee[] }[] {
  const groups: { letter: string; people: Employee[] }[] = [];
  for (const e of employees) {
    const letter = (e.displayName.trim()[0] ?? "?").toLocaleUpperCase("de-DE");
    const last = groups[groups.length - 1];
    if (last && last.letter === letter) last.people.push(e);
    else groups.push({ letter, people: [e] });
  }
  return groups;
}

/** How soon a grid that failed to load asks again, unprompted. */
const RETRY_MS = 10_000;

/**
 * Name grid, then PIN. Two taps and four digits, which is the whole
 * budget for someone standing at a cart with a scanner in one hand.
 */
export function LoginScreen({ onSignedIn }: { onSignedIn: (employee: Employee) => void }) {
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [chosen, setChosen] = useState<Employee | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  // **A failed load is not an empty shop.** Until 2026-09-26 one failed
  // fetch set the list to [] and the tablet said "No employees set up
  // yet." until the app was killed — a sign-out inside a deploy's 502
  // window was enough. The grid is what an unattended tablet shows, so
  // it also retries by itself rather than waiting for someone to notice.
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .employees()
      .then(({ employees }) => {
        if (cancelled) return;
        setEmployees(employees);
        setLoadFailed(false);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    if (!loadFailed) return;
    const id = setTimeout(() => setAttempt((n) => n + 1), RETRY_MS);
    return () => clearTimeout(id);
  }, [loadFailed, attempt]);

  const submit = async (pin: string) => {
    if (!chosen) return;
    setBusy(true);
    setError(undefined);
    try {
      const { employee } = await api.login(chosen.id, pin);
      onSignedIn(employee);
    } catch (err) {
      if (err instanceof ApiError && err.code === "too_many_attempts") {
        const mins = Math.ceil((err.retryAfterSeconds ?? 60) / 60);
        setError(`Too many attempts. Try again in ${mins} minute(s).`);
      } else if (err instanceof ApiError && err.code === "invalid_credentials") {
        setError("Wrong PIN.");
      } else {
        // Anything else — a 502 during a deploy, a dropped connection —
        // is not a verdict on the PIN. Saying "Wrong PIN." sent people
        // with the right one to fetch a lead.
        setError("Could not sign in — the server did not answer. Try again.");
      }
      setBusy(false);
    }
  };

  if (!chosen) {
    return (
      <main>
        <div className="brand-row">
          <img src="/walts-logo.png" alt="Walt's Comic Shop" className="brandmark" />
          <span className="wordmark">TaskTracker</span>
        </div>
        <div>
          <h1 className="display">Who are you?</h1>
          <p className="lede">Tap your name, then enter your PIN.</p>
        </div>
        {loadFailed && employees === null ? (
          <div className="stack tight">
            <p className="error" role="alert">
              Could not load the names — the server did not answer. Trying
              again by itself.
            </p>
            <div>
              <button type="button" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </button>
            </div>
          </div>
        ) : employees === null ? (
          <p className="muted">Loading…</p>
        ) : employees.length === 0 ? (
          <p className="muted">No employees set up yet.</p>
        ) : (
          <div className="name-groups">
            {byLetter(employees).map((group, i) => (
              <div key={`${group.letter}-${i}`} className="name-group">
                <span className="name-letter" aria-hidden="true">
                  {group.letter}
                </span>
                <div className="name-grid">
                  {group.people.map((w) => (
                    <button key={w.id} type="button" onClick={() => setChosen(w)}>
                      {w.displayName}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    );
  }

  return (
    <main className="pin-screen">
      {/* At the top, where it is found by looking for it — not under the
          keypad, where a thumb finishing a PIN lands on it by mistake. */}
      <div>
        <button
          type="button"
          className="ghost"
          onClick={() => {
            setChosen(null);
            setError(undefined);
          }}
        >
          <Icon name="back" size={26} />
          Back
        </button>
      </div>
      <div className="pin-body">
        {/* The name as large as the screen allows. Two tiles side by side
            can differ by one letter, and this is the last moment to notice
            the wrong one was tapped before work is booked to them. */}
        <div>
          <p className="eyebrow">Signing in as</p>
          <h1 className="pin-name">{chosen.displayName}</h1>
          <p className="muted">Not you? Tap Back.</p>
        </div>
        <p className="pin-prompt">Enter your PIN</p>
        <PinPad onSubmit={submit} disabled={busy} error={error} />
      </div>
    </main>
  );
}
